import { v4 as uuidv4 } from 'uuid';
import api from './api';

const VISITOR_ID_KEY = 'ft_visitor_id';

// Track recently tracked events to prevent duplicates
const recentlyTracked = new Map();
const DUPLICATE_WINDOW_MS = 1000; // Don't track same event twice within 1 second

/**
 * Get or create visitor ID from localStorage
 */
export const getVisitorId = () => {
  let visitorId = localStorage.getItem(VISITOR_ID_KEY);

  if (!visitorId) {
    visitorId = uuidv4();
    localStorage.setItem(VISITOR_ID_KEY, visitorId);
  }

  return visitorId;
};

/**
 * Track an analytics event
 */
export const trackEvent = async (eventType, { path, referrer, metadata } = {}) => {
  try {
    const visitorId = getVisitorId();
    const trackingPath = path || window.location.pathname;

    // Skip tracking for admin routes
    if (trackingPath.startsWith('/admin')) {
      return;
    }

    // Create a unique key for this event
    const eventKey = `${eventType}:${trackingPath}`;
    const now = Date.now();

    // Check if we recently tracked this exact event
    const lastTrackedTime = recentlyTracked.get(eventKey);
    if (lastTrackedTime && (now - lastTrackedTime) < DUPLICATE_WINDOW_MS) {
      // Skip duplicate tracking
      return;
    }

    // Record this tracking event
    recentlyTracked.set(eventKey, now);

    // Clean up old entries (keep map from growing indefinitely)
    if (recentlyTracked.size > 100) {
      const oldestAllowed = now - DUPLICATE_WINDOW_MS;
      for (const [key, time] of recentlyTracked.entries()) {
        if (time < oldestAllowed) {
          recentlyTracked.delete(key);
        }
      }
    }

    // Send tracking request (non-blocking)
    api.post('/analytics/track', {
      visitorId,
      eventType,
      path: trackingPath,
      referrer: referrer || document.referrer || null,
      metadata: metadata || null,
    }).catch((error) => {
      // Silently fail - don't break user experience
      console.warn('Analytics tracking failed:', error);
    });
  } catch (error) {
    // Silently fail
    console.warn('Analytics tracking error:', error);
  }
};

/**
 * Track page view
 */
export const trackPageView = (path) => {
  trackEvent('page_view', { path });
};

/**
 * Track custom event with metadata
 */
export const trackCustomEvent = (eventName, metadata) => {
  trackEvent(eventName, { metadata });
};
